/**
 * Scout v0 — QA queue panel (P18, §1.2 stage 10).
 *
 * The per-run QA queue: leads graded at stage-9 with grade, reason, evidence,
 * confidence, outreach angle, company/job/contact summary, catchall flag, and
 * history-router status. Each lead carries its prior Stage-10 review state
 * (read back from feedback rows) so the operator can see what is already
 * reviewed. Keyboard-friendly: grade hotkeys (1-4 = A-D), next/prev (j/k or
 * arrows). The decision bar enforces override-requires-reason client-side
 * (the backend enforces it too — one lane, two interfaces).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appIdentityScope } from '@/lib/identity-scope';
import { Link } from '@tanstack/react-router';
import { useQaQueue, useSubmitQaDecision } from '@/lib/scout-queries';
import { EmptyState, ErrorBanner, LoadingSpinner } from '@/components/ui';
import { getErrorMessage } from '@/lib/queries';
import type { QaGrade, QaQueueLead } from '@/types/scout';
import { QaGradeBadge } from './QaGradeBadge';
import { QaDecisionBar } from './QaDecisionBar';

function short(value: string | null | undefined, fallback = '-'): string {
  const text = value?.trim();
  if (!text) return fallback;
  return text.length > 120 ? `${text.slice(0, 117)}...` : text;
}

function evidenceCell(evidence: QaQueueLead['qa']['evidence']): string {
  if (evidence.length === 0) return '-';
  return evidence.map((e) => (e.field ? `${e.quote} [${e.field}]` : e.quote)).join(' | ');
}

function QaLeadCard({
  lead,
  index,
  total,
  isActive,
  onSelect,
}: {
  lead: QaQueueLead;
  index: number;
  total: number;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <article
      className={`card cursor-pointer transition-colors duration-fast ${isActive ? 'ring-1 ring-secondary' : 'hover:bg-surface'}`}
      aria-current={isActive || undefined}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      tabIndex={0}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <p className="text-xs text-muted font-mono">
            {index + 1}/{total} · {lead.rowRef}
          </p>
          <h3 className="text-base font-semibold text-text truncate">{short(lead.company.name, '(no company)')}</h3>
          <p className="text-xs text-muted font-mono truncate">{short(lead.company.domain)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <QaGradeBadge grade={lead.qa.grade} />
          {lead.review.state === 'reviewed' && (
            <span className="badge badge-secondary">reviewed</span>
          )}
          {lead.catchallRisk && (
            <span className="badge badge-warning" title="catchall domain - risk flag, not a kill">
              catchall
            </span>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm mb-2">
        <div>
          <dt className="text-xs text-muted">Job</dt>
          <dd className="text-text">{short(lead.jobSignal.title)}</dd>
          {lead.jobSignal.jobUrl && /^https?:\/\//i.test(lead.jobSignal.jobUrl) && (
            <dd>
              <a
                href={lead.jobSignal.jobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary hover:underline"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                Source job
              </a>
            </dd>
          )}
        </div>
        <div>
          <dt className="text-xs text-muted">Size band</dt>
          <dd className="text-text">{short(lead.company.sizeBand)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Route</dt>
          <dd className="text-text">{short(lead.routeStatus)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Posted</dt>
          <dd className="text-text">{short(lead.jobSignal.postedDate)}</dd>
        </div>
      </dl>

      <div className="space-y-1 text-sm">
        <p className="text-text">
          <span className="text-xs text-muted">Contact: </span>
          {lead.contact ? (
            <span className="text-text">
              {short(lead.contact.name)} · {short(lead.contact.title)} · {short(lead.contact.email)}
              {lead.contact.phone && <span> · Phone: {lead.contact.phone}</span>}
            </span>
          ) : (
            <span className="text-muted italic">no resolved contact</span>
          )}
        </p>
        <p className="text-text">
          <span className="text-xs text-muted">Outreach angle: </span>
          {lead.qa.outreachAngle || '-'}
        </p>
        <p className="text-text">
          <span className="text-xs text-muted">AI reason: </span>
          {lead.qa.reason || '-'}
        </p>
        <p className="text-xs text-muted font-mono break-all">
          <span className="text-muted">Evidence: </span>
          {evidenceCell(lead.qa.evidence)}
        </p>
        <p className="text-xs text-muted">
          <span className="text-muted">Confidence: </span>
          {lead.qa.confidence !== null ? `${Math.round(lead.qa.confidence * 100)}%` : '-'}
          <span className="text-muted"> · criteria </span>
          <span className="font-mono">{lead.qa.criteriaVersion}</span>
        </p>
        {lead.contactCapped && (
          <p className="text-xs text-warning">Contact discovery held at the run credit cap</p>
        )}
      </div>

      {lead.review.state === 'reviewed' && (
        <p className="text-xs text-muted mt-2 border-t border-border pt-2">
          prior: grade {lead.review.finalGrade ?? '-'} · {lead.review.finalDecision ?? '-'}
          {lead.review.reasonCode ? ` · ${lead.review.reasonCode}` : ''}
          {lead.review.note ? ` · ${lead.review.note}` : ''}
        </p>
      )}
    </article>
  );
}

export function QaQueuePanel({ runId }: { runId: string }) {
  const { data: queue, isLoading, error, refetch } = useQaQueue(runId);
  const submit = useSubmitQaDecision(runId);
  const [rawActiveIndex, setActiveIndex] = useState(0);
  const cardRefs = useRef<HTMLDivElement[]>([]);

  const leads = useMemo(() => queue?.leads ?? [], [queue]);
  // Derived clamp keeps the active index in range when the queue loads/changes
  // (no state sync — the raw index may briefly exceed a shrunken list).
  const maxIndex = Math.max(leads.length - 1, 0);
  const activeIndex = Math.min(rawActiveIndex, maxIndex);
  const activeLead = leads[activeIndex];

  const goNext = useCallback(() => {
    setActiveIndex((i) => Math.min(i + 1, Math.max(leads.length - 1, 0)));
  }, [leads.length]);

  const goPrev = useCallback(() => {
    // Clamp the raw index first so stepping back from a shrunken list moves
    // from the visible (clamped) position, not the stale raw one.
    setActiveIndex((i) => Math.max(Math.min(i, Math.max(leads.length - 1, 0)) - 1, 0));
  }, [leads.length]);

  // Keyboard navigation: j/k or arrows = next/prev, 1-4 = grade hotkeys.
  useEffect(() => {
    if (leads.length === 0) return;
    function onKey(e: KeyboardEvent) {
      // Don't hijack typing in inputs.
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;

      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [leads.length, goNext, goPrev]);

  const handleSubmit = useCallback(
    (payload: { finalGrade: QaGrade; note: string }) => {
      if (!activeLead) return;
      const isOverride = payload.finalGrade !== activeLead.qa.grade;
      // APP-W01d: generation-seat capture for the direct callback.
      const admission = appIdentityScope.current().generation;
      const submittedRow = activeLead.rowRef;
      const submittedIndex = activeIndex;
      submit.mutate(
        {
          rowRef: activeLead.rowRef,
          finalGrade: payload.finalGrade,
          // Override requires a reason — the decision bar enforces this before submit.
          ...(isOverride ? { note: payload.note } : { note: payload.note || undefined }),
        },
        {
          // Success only advances when BOTH the submitted row is still the
          // operator's active row AND the admission generation is current —
          // a stale callback (identity transition or row change) commits
          // nothing. The submitted row/index are captured so a concurrent
          // row change between submit and settle cannot advance the wrong
          // card.
          onSuccess: () => {
            if (!appIdentityScope.isCurrent(admission)) return;
            if (activeLead.rowRef !== submittedRow) return;
            setActiveIndex((i) => (
              i === submittedIndex ? Math.min(submittedIndex + 1, maxIndex) : i
            ));
          },
        },
      );
    },
    [activeLead, activeIndex, maxIndex, submit],
  );

  if (error) {
    return (
      <section className="card">
        <ErrorBanner message={getErrorMessage(error)} retryable={error.retryable} onRetry={() => refetch()} />
      </section>
    );
  }

  if (isLoading || !queue) {
    return (
      <section className="card">
        <LoadingSpinner className="py-6" />
      </section>
    );
  }

  return (
    <div className="space-y-6">
      {/* Queue summary line */}
      <section className="card" aria-labelledby="qa-queue-heading">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 id="qa-queue-heading" className="text-lg font-semibold text-text">
              QA queue
            </h2>
            <p className="text-xs text-muted mt-0.5 font-mono">
              {queue.runId} · {queue.reviewedCount}/{queue.totalLeads} reviewed
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="badge badge-secondary">{queue.totalLeads} leads</span>
            <Link
              to="/scout/qa/$runId/summary"
              params={{ runId }}
              className="text-xs text-secondary hover:underline"
            >
              Session summary →
            </Link>
          </div>
        </div>
        <p className="text-xs text-muted">
          Hotkeys: <kbd className="font-mono">j</kbd>/<kbd className="font-mono">k</kbd> next/prev ·{' '}
          <kbd className="font-mono">1</kbd>-<kbd className="font-mono">4</kbd> grade A-D ·{' '}
          <kbd className="font-mono">Enter</kbd> submit
        </p>
      </section>

      {/* Queue exhausted — every lead has a review; point the operator at the summary. */}
      {queue.totalLeads > 0 && queue.reviewedCount >= queue.totalLeads && (
        <section className="card" aria-labelledby="qa-done-heading">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 id="qa-done-heading" className="text-base font-semibold text-text">
                All {queue.totalLeads} leads reviewed
              </h3>
              <p className="text-xs text-muted mt-1">
                The QA session is complete - see the decisions, overrides, and suppression rules it created.
              </p>
            </div>
            <Link
              to="/scout/qa/$runId/summary"
              params={{ runId }}
              className="btn-primary text-sm px-4 py-2 shrink-0"
            >
              View session summary
            </Link>
          </div>
        </section>
      )}

      {leads.length === 0 ? (
        <section className="card">
          <EmptyState
            title="No leads to review"
            description="This run has no stage-9 graded leads yet. Launch or complete a run first."
          />
        </section>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Lead list */}
          <div className="space-y-3">
            {leads.map((lead, i) => (
              <div
                key={lead.rowRef}
                ref={(el) => {
                  if (el) cardRefs.current[i] = el;
                }}
              >
                <QaLeadCard
                  lead={lead}
                  index={i}
                  total={leads.length}
                  isActive={i === activeIndex}
                  onSelect={() => setActiveIndex(i)}
                />
              </div>
            ))}
          </div>

          {/* Active lead decision bar */}
          <div className="lg:sticky lg:top-4 h-fit">
            {activeLead && (
              <QaDecisionBar
                // Remount when the active lead (or its AI grade) changes so the
                // decision bar's selection/note state resets per lead.
                key={`${activeLead.rowRef}:${activeLead.qa.grade}`}
                lead={activeLead}
                aiGrade={activeLead.qa.grade}
                isSubmitting={submit.isPending}
                submitError={submit.error}
                onSubmit={handleSubmit}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
